import { useCallback, useEffect, useState } from 'react';
import { api, AdminSettings } from './adminApi';
import { inputCls, btnPrimary, btnGhost, cardCls } from './AdminPanel';

const NOTIFY_KEYS: Array<[string, string]> = [
  ['newMatchCreated', 'New match created'], ['matchInvitation', 'Match invitation'], ['rsvpReminder', 'RSVP reminder'],
  ['paymentReminder', 'Payment reminder'], ['resultPosted', 'Result posted'], ['groupAnnouncements', 'Group announcements'],
  ['inApp', 'In-app delivery'], ['push', 'Push delivery'], ['email', 'Email delivery'],
];

export default function AdminSettings({ onToast }: { onToast: (msg: string) => void }) {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [group, setGroup] = useState<Record<string, unknown>>({});
  const [notifications, setNotifications] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api<AdminSettings>('/api/admin/settings');
    if (res.ok && res.data) {
      setSettings(res.data);
      setGroup(res.data.group || {});
      setNotifications(res.data.notificationSettings || {});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    const finance = (group.finance || {}) as Record<string, unknown>;
    const res = await api('/api/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        group: {
          name: group.name, description: group.description, currency: group.currency, joinCode: group.joinCode,
          finance: { defaultTurfCost: Number(finance.defaultTurfCost) || 0, paymentMethods: String(finance.paymentMethods || '').split(',').map((s) => s.trim()).filter(Boolean) },
        },
        notificationSettings: notifications,
      }),
    });
    setBusy(false);
    if (res.ok) { onToast('Settings saved'); } else onToast(res.error || 'Save failed');
  };

  if (!settings) return <p className="text-sm text-gray-500">Loading settings…</p>;

  return (
    <div className="space-y-4">
      <div className={cardCls}>
        <h2 className="mb-3 text-sm font-bold">Group</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-xs text-gray-500">Name</span><input className={inputCls} value={String(group.name || '')} onChange={(e) => setGroup({ ...group, name: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Currency</span><input className={inputCls} value={String(group.currency || '')} onChange={(e) => setGroup({ ...group, currency: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Join code</span><input className={inputCls} value={String(group.joinCode || '')} onChange={(e) => setGroup({ ...group, joinCode: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Default turf cost (৳)</span><input className={inputCls} type="number" value={String(((group.finance || {}) as any).defaultTurfCost ?? '')} onChange={(e) => setGroup({ ...group, finance: { ...(group.finance as object || {}), defaultTurfCost: Number(e.target.value) || 0 } })} /></label>
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Payment methods (comma separated)</span><input className={inputCls} value={String(((group.finance || {}) as any).paymentMethods ?? '')} onChange={(e) => setGroup({ ...group, finance: { ...(group.finance as object || {}), paymentMethods: e.target.value } })} /></label>
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Description</span><textarea className={inputCls} rows={3} value={String(group.description || '')} onChange={(e) => setGroup({ ...group, description: e.target.value })} /></label>
        </div>
      </div>

      <div className={cardCls}>
        <h2 className="mb-3 text-sm font-bold">Notification settings</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {NOTIFY_KEYS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={Boolean(notifications[key])} onChange={(e) => setNotifications({ ...notifications, [key]: e.target.checked })} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={load}>Reload</button>
        <button className={btnPrimary} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</button>
      </div>
    </div>
  );
}